<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	ref,
	watch
} from "vue";
import { jobsStore } from "@/store/jobs";
import JobsPanelListItem from "@/components/main/jobs-panel/JobsPanelListItem.vue";

const store = jobsStore()

const jobs = computed(() => store.allJobs)
const isLoadingMore = computed(() => store.isLoadingMore)

const bottomSentinelIndex = computed(() => {
	if (jobs.value.length === 0) return -1

	return Math.max(0, jobs.value.length - 5)
})

const topSentinelIndex = computed(() => {
	if (jobs.value.length === 0) return -1

	return Math.min(4, jobs.value.length - 1)
})

const $container = ref<HTMLElement | null>(null)
const isAutoScrolling = ref(false)

let bottomObserver: IntersectionObserver | null = null
let topObserver: IntersectionObserver | null = null
let currentBottomEl: Element | null = null
let currentTopEl: Element | null = null

const loadAbovePreservingScroll = async () => {
	if (!$container.value) return

	const prevHeight = $container.value.scrollHeight
	const prevTop = $container.value.scrollTop
	await store.loadMoreJobsAbove()
	await nextTick()
	$container.value.scrollTop = prevTop + ($container.value.scrollHeight - prevHeight)
}

const bindBottomSentinel = (el: Element | null) => {
	if (el === currentBottomEl) return

	if (bottomObserver) {
		bottomObserver.disconnect()
		bottomObserver = null
	}
	currentBottomEl = el
	if (el && $container.value) {
		bottomObserver = new IntersectionObserver((entries) => {
			if (isAutoScrolling.value) return
			if (entries[0]?.isIntersecting && store.hasMoreBelow) {
				store.loadMoreJobsBelow()
			}
		}, {
			root: $container.value,
			threshold: 0,
		})
		bottomObserver.observe(el)
	}
}

const bindTopSentinel = (el: Element | null) => {
	if (el === currentTopEl) return

	if (topObserver) {
		topObserver.disconnect()
		topObserver = null
	}
	currentTopEl = el
	if (el && $container.value) {
		topObserver = new IntersectionObserver((entries) => {
			if (isAutoScrolling.value) return
			if (entries[0]?.isIntersecting && store.hasMoreAbove) {
				loadAbovePreservingScroll()
			}
		}, {
			root: $container.value,
			threshold: 0,
		})
		topObserver.observe(el)
	}
}

watch(() => store.jobsLoading, (newVal) => {
	if (newVal && !store.highlightedJobId) {
		$container.value?.scrollTo({ top: 0 })
	}
})

watch(() => store.highlightedJobId, async (id) => {
	if (!id) return

	await nextTick()
	const el = $container.value?.querySelector<HTMLElement>(`[data-job-id="${id}"]`)
	if (!el) return

	isAutoScrolling.value = true
	el.scrollIntoView({ block: 'center', behavior: 'smooth' })
	setTimeout(() => { isAutoScrolling.value = false }, 700)

	el.classList.add('jobs-panel-list-item--highlight')
	const onEnd = () => {
		el.classList.remove('jobs-panel-list-item--highlight')
		el.removeEventListener('animationend', onEnd)
		store.clearHighlightedJob()
	}
	el.addEventListener('animationend', onEnd)
}, { immediate: true })

onBeforeUnmount(() => {
	bottomObserver?.disconnect()
	topObserver?.disconnect()
	bottomObserver = null
	topObserver = null
	currentBottomEl = null
	currentTopEl = null
})
</script>

<template>
	<div ref="$container" class="jobs-panel-list">
		<template
			v-for="(job, index) in jobs"
			:key="job.Id"
		>
			<div
				v-if="index === topSentinelIndex"
				:ref="(el: Element | null) => bindTopSentinel(el)"
				class="jobs-panel-list__sentinel"
				aria-hidden="true"
			/>
			<jobs-panel-list-item :job="job"/>
			<div
				v-if="index + 1 < jobs.length"
				class="jobs-panel-list__divider"
			/>
			<div
				v-if="index === bottomSentinelIndex"
				:ref="(el: Element | null) => bindBottomSentinel(el)"
				class="jobs-panel-list__sentinel"
				aria-hidden="true"
			/>
		</template>
		<div
			v-if="isLoadingMore"
			class="jobs-panel-list__loader-more"
		>
			Загрузка...
		</div>
	</div>
</template>

<style scoped lang="scss">
.jobs-panel-list {
	display: flex;
	flex-direction: column;
	position: relative;
	padding: 8px 0 8px 8px;
	overflow: auto;

	&__divider {
		width: 100%;
		border-bottom: 1px solid #F2F2F2;
		margin: 8px 0;
	}

	&__sentinel {
		width: 100%;
		height: 1px;
		pointer-events: none;
	}

	&__loader-more {
		padding: 8px 0;
		text-align: center;
		font-size: 13px;
		color: #979797;
	}
}
</style>
